import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Paper,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import splashImages from '../../mocks/splashImages.json';
import { useApp } from '../../contexts/AppContext';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { resolveStoreAssetUrl } from '../../utils/storeAssets';

const Splash = () => {
  const { isMobile } = useApp();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (!isMobile) {
      // Auto-play for desktop
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => 
          prev === splashImages.images.length - 1 ? 0 : prev + 1
        );
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [isMobile]);

  const handleNext = () => {
    if (currentImageIndex < splashImages.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const handleSkip = () => {
    navigate('/auth', { replace: true });
  };

  const handleGetStarted = () => {
    navigate('/auth', { replace: true });
  };

  const currentImage = splashImages.images[currentImageIndex];
  const imageBackground = currentImage.background || '#FFFFFF';
  const isLastSlide = currentImageIndex === splashImages.images.length - 1;
  const progressWidth = `${((currentImageIndex + 1) / splashImages.images.length) * 100}%`;
  const mobileShellBackground = `
    radial-gradient(circle at 50% 8%, rgba(244, 114, 182, 0.28) 0%, rgba(244, 114, 182, 0) 34%),
    radial-gradient(circle at 18% 22%, rgba(96, 165, 250, 0.28) 0%, rgba(96, 165, 250, 0) 32%),
    linear-gradient(135deg, #060d1f 0%, #0d1b3e 44%, #17144a 100%)
  `;

  return (
    <Box
      sx={{
        height: '100dvh',
        minHeight: '-webkit-fill-available',
        maxHeight: '100dvh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        bgcolor: isMobile ? '#060d1f' : imageBackground,
        background: isMobile ? mobileShellBackground : imageBackground
      }}
    >
      {/* Image Section */}
      <Box
        sx={{
          flex: isMobile ? '1 1 auto' : 1,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: isMobile ? '100%' : 'auto',
          minHeight: 0,
          p: isMobile
            ? 'max(14px, env(safe-area-inset-top)) 16px 10px'
            : 0,
          bgcolor: isMobile ? 'transparent' : imageBackground
        }}
      >
        <AnimatePresence mode="wait">
          <Box
            key={currentImage.id}
            component={motion.img}
            src={resolveStoreAssetUrl(currentImage.url)}
            alt={currentImage.title}
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: -10 }}
            transition={{ duration: 0.34, ease: 'easeOut' }}
            sx={{
              width: isMobile ? 'min(100%, calc(100dvh - 126px))' : '100%',
              maxWidth: isMobile ? 560 : '100%',
              height: isMobile ? 'auto' : '100%',
              maxHeight: isMobile ? 'calc(100dvh - 126px)' : '100%',
              aspectRatio: '1 / 1',
              objectFit: 'contain',
              display: 'block',
              bgcolor: imageBackground,
              borderRadius: isMobile ? 3 : 0,
              boxShadow: isMobile
                ? '0 24px 80px rgba(0,0,0,0.38), 0 0 0 1px rgba(255,255,255,0.16)'
                : 'none'
            }}
          />
        </AnimatePresence>
      </Box>

      {/* Controls Section */}
      {isMobile ? (
        <Box
          sx={{
            flex: '0 0 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            px: 2,
            pb: 'calc(10px + env(safe-area-inset-bottom))'
          }}
        >
          {/* Dot Navigation */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 0.75,
              py: 1,
              flexShrink: 0
            }}
          >
            {splashImages.images.map((_, index) => (
              <IconButton
                key={index}
                size="small"
                onClick={() => setCurrentImageIndex(index)}
                sx={{
                  p: 0.25,
                  color: index === currentImageIndex ? '#f472b6' : 'rgba(255,255,255,0.34)',
                  '& svg': {
                    fontSize: index === currentImageIndex ? 16 : 13,
                    filter: index === currentImageIndex ? 'drop-shadow(0 0 10px rgba(244,114,182,0.75))' : 'none',
                    transition: 'all 0.2s ease'
                  }
                }}
              >
                <FiberManualRecordIcon fontSize="small" />
              </IconButton>
            ))}
          </Box>
          <Paper
            elevation={0}
            sx={{
              flexShrink: 0,
              bgcolor: 'rgba(255,255,255,0.1)',
              color: '#FFFFFF',
              border: '1px solid rgba(255,255,255,0.16)',
              borderRadius: 3,
              boxShadow: '0 18px 48px rgba(0,0,0,0.34)',
              backdropFilter: 'blur(22px)',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                height: 3,
                width: '100%',
                bgcolor: 'rgba(255,255,255,0.14)'
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: progressWidth,
                  background: 'linear-gradient(90deg, #60a5fa 0%, #f472b6 100%)',
                  transition: 'width 0.3s ease'
                }}
              />
            </Box>
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              <Button
                onClick={handleSkip}
                sx={{
                  minHeight: 46,
                  px: 2,
                  color: 'rgba(255,255,255,0.86)',
                  fontWeight: 700,
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: '#FFFFFF'
                  }
                }}
              >
                Skip
              </Button>
              <Button
                variant="contained"
                onClick={isLastSlide ? handleGetStarted : handleNext}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  minHeight: 50,
                  px: isLastSlide ? 2.75 : 3.25,
                  borderRadius: 2,
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #C73866 0%, #FB5B73 100%)',
                  boxShadow: '0 10px 28px rgba(199,56,102,0.42)',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #A72F56 0%, #C73866 100%)',
                    boxShadow: '0 14px 34px rgba(199,56,102,0.52)'
                  }
                }}
              >
                {isLastSlide ? 'Get Started' : 'Next'}
              </Button>
            </Box>
          </Paper>
        </Box>
      ) : (
        <Box
          sx={{
            width: '40%',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 2,
            background: mobileShellBackground,
            borderLeft: '1px solid rgba(255,255,255,0.12)'
          }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={handleGetStarted}
            endIcon={<ArrowForwardIcon />}
            sx={{
              alignSelf: 'flex-start',
              px: 4,
              py: 1.5,
              fontWeight: 800,
              background: 'linear-gradient(135deg, #C73866 0%, #FB5B73 100%)'
            }}
          >
            Get Started
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Splash; 
